import React, { useEffect, useRef, useState } from "react";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import Styles from "./styles.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
    getPartnersConvertHistory,
    setCabinetPartnersConvertPointsHistoryModalStatus
} from "../../../store/cabinet/action";

import convertHistoryIcon from "../../../theme/assets/svg/b_cabinet_profile_convert_history_icon.svg";

const formatNumber = (value, maxDigits = 8) => {
    const numberValue = Number(value);

    if (!Number.isFinite(numberValue)) return "0";

    return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: maxDigits,
    }).format(numberValue);
};

const formatDate = (dateValue) => {
    if (!dateValue) return "—";

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) return "—";

    return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
    }).format(date);
};

const formatTime = (dateValue) => {
    if (!dateValue) return "—";

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) return "—";

    return new Intl.DateTimeFormat("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    }).format(date);
};

const normalizeStatus = (status) => {
    const normalizedStatus = String(status || "").toLowerCase();

    if (
        normalizedStatus === "new" ||
        normalizedStatus === "pending" ||
        normalizedStatus === "completed" ||
        normalizedStatus === "failed" ||
        normalizedStatus === "rejected"
    ) {
        return normalizedStatus;
    }

    return "new";
};

const getStatusLabel = (status) => {
    const normalizedStatus = normalizeStatus(status);

    const statusLabels = {
        new: "Новая",
        pending: "В обработке",
        completed: "Завершена",
        failed: "Ошибка",
        rejected: "Отклонена",
    };

    return statusLabels[normalizedStatus] || status;
};

const CabinetPartnersConvertPointsHistoryModal = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const isFetchingRef = useRef(false);
    const requestedPageRef = useRef(1);

    const modalStatus = useSelector(store => store.cabinet.cabinetPartnersConvertPointsHistoryModalStatus);
    const partnersConvertHistory = useSelector(store => store.cabinet.partnersConvertHistory);
    const partnersConvertHistoryStarted = useSelector(store => store.cabinet.partnersConvertHistoryStarted);

    const [historyItems, setHistoryItems] = useState([]);
    const [historyMeta, setHistoryMeta] = useState({});
    const [currentPage, setCurrentPage] = useState(1);

    const lastPage = Number(historyMeta?.last_page || 1);
    const hasMorePages = currentPage < lastPage;

    const closeModal = () => {
        dispatch(setCabinetPartnersConvertPointsHistoryModalStatus(false));
    };

    const loadPage = (page) => {
        if (isFetchingRef.current) return;
        if (partnersConvertHistoryStarted) return;

        isFetchingRef.current = true;
        requestedPageRef.current = page;

        dispatch(getPartnersConvertHistory(page));
    };

    const historyScrollHandler = (event) => {
        if (!hasMorePages) return;
        if (isFetchingRef.current) return;
        if (partnersConvertHistoryStarted) return;

        const target = event.currentTarget;
        const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight;

        if (distanceToBottom > 80) return;

        loadPage(currentPage + 1);
    };

    useEffect(() => {
        if (!modalStatus) return;

        setHistoryItems([]);
        setHistoryMeta({});
        setCurrentPage(1);

        isFetchingRef.current = true;
        requestedPageRef.current = 1;

        dispatch(getPartnersConvertHistory(1));
    }, [modalStatus, dispatch]);

    useEffect(() => {
        if (!modalStatus) return;
        if (!partnersConvertHistory) return;

        const responseItems = Array.isArray(partnersConvertHistory?.items)
            ? partnersConvertHistory.items
            : [];

        const responseMeta = partnersConvertHistory?.meta || {};
        const responsePage = Number(responseMeta?.current_page || requestedPageRef.current || 1);

        setHistoryMeta(responseMeta);
        setCurrentPage(responsePage);

        setHistoryItems((prevState) => {
            if (responsePage <= 1) {
                return responseItems;
            }

            const existingIds = new Set(prevState.map((item) => item.id));

            const newItems = responseItems.filter((item) => {
                return !existingIds.has(item.id);
            });

            return [
                ...prevState,
                ...newItems,
            ];
        });

        isFetchingRef.current = false;
    }, [partnersConvertHistory, modalStatus]);

    useEffect(() => {
        if (partnersConvertHistoryStarted) return;

        isFetchingRef.current = false;
    }, [partnersConvertHistoryStarted]);

    useEffect(() => {
        if (modalStatus) return;

        setHistoryItems([]);
        setHistoryMeta({});
        setCurrentPage(1);

        isFetchingRef.current = false;
        requestedPageRef.current = 1;
    }, [modalStatus]);

    return (
        <Popup
            open={!!modalStatus}
            modal
            nested
            closeOnDocumentClick={true}
            onClose={closeModal}
            className="cabinetPartnersConvertPointsHistoryModal"
        >
            <div className={Styles.modalContainer}>
                <div
                    className={Styles.modalCloseBtn}
                    onClick={closeModal}
                >
                    <div />
                    <div />
                </div>

                <div className={Styles.modalContentWrap}>
                    <div className={Styles.titleRow}>
                        <div className={Styles.titleIcon}>
                            <img src={convertHistoryIcon} alt={t("История конвертации баллов")} />
                        </div>

                        <div className={Styles.titleTextWrap}>
                            <div className={Styles.title}>
                                {t("История конвертации баллов")}
                            </div>

                            <div className={Styles.note}>
                                {t("Следите за своими конвертациями")}
                            </div>
                        </div>
                    </div>

                    <div className={Styles.historyWrap}>
                        <div className={Styles.historyHead}>
                            <div>{t("Дата и время")}</div>
                            <div>{t("Кол-во")}</div>
                            <div>{t("Курс обмена")}</div>
                            <div>{t("Статус")}</div>
                        </div>

                        <div
                            className={Styles.historyScroll}
                            onScroll={historyScrollHandler}
                        >
                            {partnersConvertHistoryStarted && !historyItems.length ? (
                                <div className={Styles.emptyState}>
                                    {t("Загрузка")}
                                </div>
                            ) : historyItems.length > 0 ? (
                                <>
                                    {historyItems.map((item) => {
                                        const status = normalizeStatus(item?.status);

                                        return (
                                            <div className={Styles.historyRow} key={item.id}>
                                                <div className={`${Styles.historyCell} ${Styles.dateCell}`}>
                                                    <div className={Styles.mobileLabel}>
                                                        {t("Дата и время")}
                                                    </div>

                                                    <div className={Styles.dateValue}>
                                                        {formatDate(item?.created_at)}
                                                    </div>

                                                    <div className={Styles.timeValue}>
                                                        {formatTime(item?.created_at)}
                                                    </div>
                                                </div>

                                                <div className={`${Styles.historyCell} ${Styles.amountCell}`}>
                                                    <div className={Styles.mobileLabel}>
                                                        {t("Кол-во")}
                                                    </div>

                                                    <div className={`${Styles.amountValue} ${Styles[`amountValue_${status}`] || ""}`}>
                                                        +{formatNumber(item?.amount_usdt, 6)} USD
                                                    </div>

                                                    <div className={Styles.pointsValue}>
                                                        - {formatNumber(item?.points, 6)} points
                                                    </div>
                                                </div>

                                                <div className={`${Styles.historyCell} ${Styles.rateCell}`}>
                                                    <div className={Styles.mobileLabel}>
                                                        {t("Курс обмена")}
                                                    </div>

                                                    <div className={`${Styles.rateValue} ${Styles[`rateValue_${status}`] || ""}`}>
                                                        {formatNumber(item?.points_to_usd, 6)} USD
                                                    </div>
                                                </div>

                                                <div className={`${Styles.historyCell} ${Styles.statusCell}`}>
                                                    <div className={Styles.mobileLabel}>
                                                        {t("Статус")}
                                                    </div>

                                                    <div className={`${Styles.statusBadge} ${Styles[`statusBadge_${status}`] || ""}`}>
                                                        {t(getStatusLabel(item?.status))}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {partnersConvertHistoryStarted && (
                                        <div className={Styles.loadingMore}>
                                            {t("Загрузка")}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className={Styles.emptyState}>
                                    {t("История конвертаций пока пуста")}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Popup>
    );
};

export default CabinetPartnersConvertPointsHistoryModal;
